import {createAsyncThunk} from '@reduxjs/toolkit'
import {go, RouterState} from 'redux-first-history'

import {changeLocation} from '@web/actions/application'
import {
    DefaultAddressBookRequestFailMessage,
    DELIVERY_MANAGE_SCENE,
    ZipcodeSearch,
    zipCodeSearchKeyordMinLength,
    zipCodeSearchKeyordMinLengthMessage,
} from '@web/core/constants'
import {
    Address,
    AddressSearchQuery,
    DeliveryAddress,
    deliveryManagerCaseSelector,
    deliveryManagerSelector,
    type DeliveryManagerState,
    getEmptyDeliveryAddress,
    getInitialAddressSearchQuery,
    saveDeliveryManagerSessionStorage,
    saveStoragePayloadSelector,
} from '@web/slices/deliveryManager/slice'
import {locationSelector, push} from '@web/slices/router'

import apis from './apis/searchMaps'
import {increaseHistoryCountFromBase} from './utils'

export const goZipCodeSearch = createAsyncThunk<
    void,
    void,
    {
        state: {
            router: RouterState
        }
    }
>('deliveryManager/goZipCodeSearch', (_, {dispatch, getState}) => {
    const {pathname, search, state: locationState} = locationSelector(getState())
    const url = `${pathname}${search}`

    dispatch(
        push(url, {
            state: {
                ...locationState,
                deliveryManagerScene: DELIVERY_MANAGE_SCENE.SEARCH,
                historyCountFromBase: increaseHistoryCountFromBase(locationState?.historyCountFromBase),
            },
        }),
    )
})

/** 초기검색화면에서 상세주소입력화면으로 가면서 쌓이는 히스토리카운트 */
const HISTORY_COUNT_FROM_SEARCH = 2
/** 우편번호검색리스트, 상세주소입력화면에서 초기검색화면으로 돌아가는 액션 */
export const goBackToSearch = createAsyncThunk<
    AddressSearchQuery,
    void,
    {
        state: {
            router: RouterState
            deliveryManager: DeliveryManagerState
        }
    }
>('deliveryManager/goBackToSearch', (_, {dispatch, getState}) => {
    const {
        status: {scene},
        addressSearch: {
            query: {allowSixDigitCode},
        },
    } = deliveryManagerSelector(getState())

    const searchQueryPayload: AddressSearchQuery = {...getInitialAddressSearchQuery(), allowSixDigitCode}

    const saveStoragePayload = saveStoragePayloadSelector(getState())
    saveDeliveryManagerSessionStorage({
        ...saveStoragePayload,
        searchQuery: searchQueryPayload,
    })

    if (scene === DELIVERY_MANAGE_SCENE.DETAIL_ADDRESS) {
        dispatch(go(-1 * HISTORY_COUNT_FROM_SEARCH))
    } else if (DELIVERY_MANAGE_SCENE.RESULT) {
        dispatch(changeLocation({type: 'BACK'}))
    }

    return searchQueryPayload
})

/** 주소검색 키워드 저장 */
export const changeZipCodeSearchInfo = createAsyncThunk<
    {keyword: string; page: number; regionCode: Maybe<string>},
    {keyword: string; page?: number; regionCode?: Maybe<string>},
    {
        state: {
            router: RouterState
            deliveryManager: DeliveryManagerState
        }
    }
>(
    'deliveryManager/changeZipCodeSearchInfo',
    ({keyword, page = 1, regionCode = null}, {dispatch, rejectWithValue, getState}) => {
        const {pathname, search, state: locationState} = locationSelector(getState())

        if (!keyword || keyword?.length < zipCodeSearchKeyordMinLength) {
            return rejectWithValue(zipCodeSearchKeyordMinLengthMessage)
        }

        const url = `${pathname}${search}`
        const zipCodeSearchInfo = {
            keyword,
            page,
            regionCode,
        }

        // 최초검색 시 이동 및 스토어 업데이트, 재검색 시 스토어만 업데이트
        if (locationState.deliveryManagerScene !== DELIVERY_MANAGE_SCENE.RESULT) {
            dispatch(
                push(url, {
                    state: {
                        ...locationState,
                        deliveryManagerScene: DELIVERY_MANAGE_SCENE.RESULT,
                        historyCountFromBase: increaseHistoryCountFromBase(locationState?.historyCountFromBase),
                    },
                }),
            )
        }

        const saveStoragePayload = saveStoragePayloadSelector(getState())
        saveDeliveryManagerSessionStorage({
            ...saveStoragePayload,
            searchQuery: {...saveStoragePayload.searchQuery, keyword, page, regionCode},
        })

        return zipCodeSearchInfo
    },
)

type SearchZipcodeActionPayload = {
    /** 검색어 */
    searchKeyword: string
    /** 페이지네이션 */
    page?: number
    /** 검색창/리스트 사이 광역시도코드 selectbox 선택값 */
    regionCode?: string
    /** 6자리 우편번호 허용 여부 */
    allowSixDigit?: boolean
    /** 우편번호 검색 시 로그인 여부 */
    revealed?: boolean
}

type SearchZipcodeReturnValue = {
    /** 검색된 우편번호 수 */
    totalCount: number
    /** 광역시도별 검색된 우편번호 수 */
    regionCountItems: {count: number; name: string; code: string}[]
    /** 검색 결과 */
    results: Address[]
}

export const searchZipcode = createAsyncThunk<SearchZipcodeReturnValue, SearchZipcodeActionPayload>(
    'deliveryManager/searchZipcode',
    async ({searchKeyword, page = 1, regionCode, allowSixDigit, revealed = false}, {rejectWithValue}) => {
        const offset = (page - 1) * ZipcodeSearch.LIST_MAX_LENGTH_PER_PAGE

        try {
            const {meta, results} = await apis.searchZipCode({
                keyword: searchKeyword,
                offset,
                allowSixDigit,
                revealed,
                ...(regionCode ? {regionCode} : {}),
            })

            return {
                totalCount: meta?.totalDocs ?? 0,
                regionCountItems:
                    meta?.regionCountItems?.map(({count, regionCode: code, regionName: name}) => {
                        return {count, name, code}
                    }) ?? [],
                results:
                    results?.map((apiResult) => {
                        return {
                            ...apiResult,
                            buildingManagementNo: apiResult.buildingId,
                            locationX: apiResult.x,
                            locationY: apiResult.y,
                            zipcode: apiResult.zipCode,
                            legalRegionCode: apiResult.bDongCode,
                            detailAddress: null,
                        }
                    }) ?? [],
            }
        } catch (e) {
            if (e instanceof Error) {
                return rejectWithValue({message: e.message}) // 여기서 에러를 받아서 리턴.
            } else {
                return rejectWithValue({message: DefaultAddressBookRequestFailMessage})
            }
        }
    },
)

export const selectZipCode = createAsyncThunk<
    Maybe<Address>,
    {
        roadAddress: string
        jibunAddress: string
        roadZipcode: string
        zipcode: string
        legalRegionCode: string
        roadGroupId: string
        buildingNum: string
        buildingManagementNo: Maybe<string>
        locationX: Maybe<string>
        locationY: Maybe<string>
    },
    {
        state: {
            router: RouterState
            deliveryManager: DeliveryManagerState
        }
    }
>('deliveryManager/selectZipCode', (zipCodeSelectInfo, {dispatch, getState}) => {
    const {pathname, search, state: locationState} = locationSelector(getState())
    const url = `${pathname}${search}`

    const address = {...zipCodeSelectInfo, detailAddress: ''}

    const saveStoragePayload = saveStoragePayloadSelector(getState())
    saveDeliveryManagerSessionStorage({...saveStoragePayload, address})

    dispatch(
        push(url, {
            state: {
                ...locationState,
                deliveryManagerScene: DELIVERY_MANAGE_SCENE.DETAIL_ADDRESS,
                historyCountFromBase: increaseHistoryCountFromBase(locationState?.historyCountFromBase),
            },
        }),
    )

    return address
})

/**
 * 우편번호 검색결과 전달
 * rurl 이 있는 경우에는 rurl에 정보를 담아서 이동
 * key가 있는 경우에는 전달받은 key값과 함께 postmessage 전송 후 CLOSE
 */
export const sendZipCodeResult = createAsyncThunk<
    Maybe<{address: Address; deliveryAddress: DeliveryAddress}>,
    {
        detailAddress?: string
    },
    {
        state: {
            router: RouterState
            deliveryManager: DeliveryManagerState
        }
    }
>('deliveryManager/sendZipCodeResult', ({detailAddress = ''}, {dispatch, getState}) => {
    const {pathname, search, state: locationState} = locationSelector(getState())
    const {address, deliveryAddress} = deliveryManagerSelector(getState())

    if (!address) {
        return null
    }

    const addressPayload = {...address, detailAddress}
    const deliveryAddressPayload = {
        ...getEmptyDeliveryAddress(),
        ...deliveryAddress,
        isRoadNameAddress: !!address.roadZipcode,
        baseAddress: address.roadAddress || address.jibunAddress,
        zipCode: address.roadZipcode || address.zipcode,
        buildingManagementNo: address.buildingManagementNo,
        locationX: address.locationX,
        locationY: address.locationY,
        detailAddress,
    }

    const saveStoragePayload = saveStoragePayloadSelector(getState())
    saveDeliveryManagerSessionStorage({...saveStoragePayload, deliveryAddress: deliveryAddressPayload})

    const {isZipcodeCase} = deliveryManagerCaseSelector(getState())
    // 우편번호 검색 진입 케이스가 아닐 때만, 배송지수정화면으로 이어서 넘어간다.
    if (!isZipcodeCase) {
        const url = `${pathname}${search}`
        dispatch(
            push(url, {
                state: {
                    ...locationState,
                    deliveryManagerScene: DELIVERY_MANAGE_SCENE.FORM,
                    historyCountFromBase: increaseHistoryCountFromBase(locationState?.historyCountFromBase),
                },
            }),
        )
    }

    return {address: addressPayload, deliveryAddress: deliveryAddressPayload}
})
